import { useEffect, useState } from 'react';
import {
  ArrowSquareOutIcon,
  CheckCircleIcon,
  LockIcon,
  ShieldCheckIcon,
  SignatureIcon,
  SpinnerIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';

import {
  forwardWalletBatch,
  getSetupApprovalsTypedData,
  type SetupApprovalsTypedDataResponse,
} from '../api';
import { useAuth } from '../auth';
import { useT } from '../i18n';
import { markApprovalsDone } from '../lib/approvals-flag';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { tryGetTelegram } from '../telegram';
import { getSigner } from '../wallet';

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: SetupApprovalsTypedDataResponse }
  | { kind: 'signing' }
  | { kind: 'submitting' }
  | { kind: 'submitted'; transactionHash: string; state: string };

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

export default function Approvals() {
  const auth = useAuth();
  const t = useT();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useTelegramBackButton();
  useNavStackEntry({ label: t('approvals.breadcrumb') });

  useEffect(() => {
    if (auth.state !== 'ready') return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getSetupApprovalsTypedData(auth.auth.externalUserId);
        if (!cancelled) setPhase({ kind: 'ready', data });
      } catch (err) {
        if (!cancelled)
          setPhase({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.state, auth.state === 'ready' ? auth.auth.externalUserId : null]);

  async function onSign() {
    if (phase.kind !== 'ready' || auth.state !== 'ready') return;
    const { typedData, args } = phase.data;
    try {
      setPhase({ kind: 'signing' });
      const signer = await getSigner();
      const types = { ...typedData.types };
      delete (types as Record<string, unknown>)['EIP712Domain'];
      const signature = await signer.signTypedData(
        typedData.domain as never,
        types,
        typedData.message,
      );

      setPhase({ kind: 'submitting' });
      const resp = await forwardWalletBatch(auth.auth.externalUserId, {
        type: 'WALLET',
        from: args.from,
        to: args.factoryAddress,
        nonce: args.nonce,
        signature,
        depositWalletParams: {
          depositWallet: args.walletAddress,
          deadline: args.deadline,
          calls: args.calls,
        },
      });
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('success');
      markApprovalsDone(args.walletAddress);
      setPhase({
        kind: 'submitted',
        transactionHash: resp.transactionHash,
        state: resp.state,
      });
    } catch (err) {
      tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('error');
      setPhase({ kind: 'error', message: (err as Error).message });
    }
  }

  if (auth.state === 'loading') {
    return (
      <AuthLoadingScreen
        title="Preparing approvals"
        description="Authenticating your Telegram session before quoting the on-chain setup."
        shortDescription="Authenticating your session."
      />
    );
  }
  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow="Approvals" title="Enable trading">
        <Alert tone="danger">
          <AlertTitle>Authentication failed</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }
  if (phase.kind === 'loading') {
    return (
      <AuthLoadingScreen
        title="Preparing approvals"
        description="Asking the relayer for the latest CTF / NegRisk approval payload."
        shortDescription="Quoting approvals."
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: 'Wallet', color: 'bg-sky-500', delay: '0.4s' },
          { label: 'Polymarket', color: 'bg-primary', delay: '0.8s' },
        ]}
      />
    );
  }

  if (phase.kind === 'error') {
    return (
      <ScreenShell eyebrow="Approvals" title="Enable trading">
        <Alert tone="danger">
          <AlertTitle>Approval setup failed</AlertTitle>
          <AlertDescription>{phase.message}</AlertDescription>
        </Alert>
        <Button
          variant="outline"
          size="lg"
          onClick={() => tryGetTelegram()?.close?.()}
        >
          Back to chat
        </Button>
      </ScreenShell>
    );
  }

  if (phase.kind === 'submitted') {
    const url = phase.transactionHash
      ? `https://polygonscan.com/tx/${phase.transactionHash}`
      : null;
    return (
      <ScreenShell eyebrow="Approvals" title="Trading enabled">
        <Card>
          <CardHeader>
            <CardEyebrow>
              <CheckCircleIcon weight="fill" className="size-3" />
              Submitted
            </CardEyebrow>
            <CardTitle>Your deposit wallet can now trade</CardTitle>
            <CardDescription>
              Wait a few seconds and reopen the order in the chat — Polymarket
              will accept it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <KeyValue label="State" value={<Badge tone="success">{phase.state}</Badge>} />
          </CardContent>
          <CardFooter>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: 'outline', size: 'lg' })}
              >
                <ArrowSquareOutIcon weight="bold" className="size-4" />
                View on Polygonscan
              </a>
            )}
            <Button size="lg" onClick={() => tryGetTelegram()?.close?.()}>
              Back to chat
            </Button>
          </CardFooter>
        </Card>
      </ScreenShell>
    );
  }

  const data = phase.kind === 'ready' ? phase.data : null;
  const busy = phase.kind === 'signing' || phase.kind === 'submitting';

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheckIcon weight="fill" className="size-3" />
          Approvals
        </span>
      }
      title="Enable trading"
      description="One-time setup. Approves Polymarket's Exchange, NegRiskExchange and NegRiskAdapter to move USDC.e and outcome tokens from your deposit wallet — gasless via the relayer."
    >
      <NavBar />
      <Card>
        <CardHeader>
          <CardEyebrow>
            <LockIcon weight="fill" className="size-3" />
            On-chain setup
          </CardEyebrow>
          <CardTitle>{data ? `${data.args.calls.length} approvals` : '—'}</CardTitle>
          <CardDescription>
            Each call grants a specific Polymarket contract permission to move
            tokens from your deposit wallet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <KeyValue label="Wallet" value={data ? short(data.args.walletAddress) : '—'} mono />
          <KeyValue label="Calls" value={data ? `${data.args.calls.length}` : '—'} />
          <KeyValue label="Gas" value={<Badge tone="success">Gasless</Badge>} />
        </CardContent>
        <CardFooter>
          <Button size="lg" onClick={onSign} disabled={phase.kind !== 'ready'}>
            {busy ? (
              <SpinnerIcon weight="bold" className="size-4 animate-spin" />
            ) : (
              <SignatureIcon weight="bold" className="size-4" />
            )}
            {phase.kind === 'signing'
              ? 'Signing…'
              : phase.kind === 'submitting'
                ? 'Submitting…'
                : 'Sign & enable'}
          </Button>
        </CardFooter>
      </Card>
    </ScreenShell>
  );
}
